import * as React from 'react';
import styled, { css } from 'styled-components';
import { rem } from 'polished';
import { __ } from '@bart.sk-eshop/react-i18n';
import CategorySidebarFilters, {
  Attrib,
  DeliveryCounts,
  FilterCounts,
} from './CategorySidebarFilters';
import { XIcon } from '../../_layout/Header/HeaderNew/icons';
import { isSsr } from '../../../utilities';

interface MobileFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  attribs: Attrib[];
  selectedParameters: string[];
  onParametersChange: (parameters: string[]) => void;
  priceBounds: { min: number; max: number };
  priceValue: { min: string; max: string };
  onPriceChange: (min: string, max: string) => void;
  selectedDeliveryDays: string[];
  deliveryCounts: DeliveryCounts | null;
  deliveryCountsBase: DeliveryCounts | null;
  onDeliveryDaysChange: (deliveryDays: string[]) => void;
  selectedFlags: string[];
  flagCounts: FilterCounts | null;
  flagCountsBase: FilterCounts | null;
  onFlagsChange: (flags: string[]) => void;
}

function MobileFilterDrawer(props: MobileFilterDrawerProps) {
  const { open, onClose, ...filterProps } = props;

  React.useEffect(() => {
    if (isSsr() || typeof document === 'undefined') {
      return;
    }
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.body.classList.add('hide-smartsupp');
      const smartsupp = (window as any).smartsupp;
      if (typeof smartsupp === 'function') {
        smartsupp('chat:hide');
      }
      return () => {
        document.body.style.overflow = prev;
        document.body.classList.remove('hide-smartsupp');
        if (typeof (window as any).smartsupp === 'function') {
          (window as any).smartsupp('chat:show');
        }
      };
    }
    return;
  }, [open]);

  return (
    <>
      <Backdrop open={open} onClick={onClose} />
      <Panel open={open} aria-hidden={!open}>
        <Header>
          <Title>{__('Filtrovať')}</Title>
          <CloseButton
            type="button"
            onClick={onClose}
            aria-label={__('Zavrieť')}
          >
            <XIcon />
          </CloseButton>
        </Header>
        <Body>
          <CategorySidebarFilters {...filterProps} />
        </Body>
        <Footer>
          <ApplyButton type="button" onClick={onClose}>
            {__('Použiť filter')}
          </ApplyButton>
        </Footer>
      </Panel>
    </>
  );
}

const Backdrop = styled.div<{ open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1290;
  background: rgba(0, 0, 0, 0.4);
  opacity: ${({ open }) => (open ? 1 : 0)};
  visibility: ${({ open }) => (open ? 'visible' : 'hidden')};
  transition: opacity 0.25s ease,
    visibility 0s linear ${({ open }) => (open ? '0s' : '0.25s')};
  ${({ theme }) => theme.media('md')} {
    display: none;
  }
`;

const Panel = styled.div<{ open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  height: 100dvh;
  width: 86%;
  max-width: ${rem(420)};
  z-index: 1300;
  background: ${({ theme }) => theme.color.white};
  display: flex;
  flex-direction: column;
  box-shadow: -${rem(8)} 0 ${rem(32)} rgba(0, 0, 0, 0.25);
  transform: translateX(100%);
  visibility: hidden;
  transition: transform 0.25s ease, visibility 0s linear 0.25s;
  ${({ open }) =>
    open &&
    css`
      transform: translateX(0);
      visibility: visible;
      transition: transform 0.25s ease;
    `}
  ${({ theme }) => theme.media('md')} {
    display: none;
  }
`;

const Header = styled.div`
  flex: 0 0 auto;
  background: ${({ theme }) => theme.color.primary};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${rem(16)};
  height: ${rem(56)};
`;

const Title = styled.span`
  color: ${({ theme }) => theme.color.white};
  font-size: ${rem(18)};
  font-weight: 700;
  line-height: 1;
`;

const CloseButton = styled.button`
  appearance: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(36)};
  height: ${rem(36)};
  border-radius: 50%;
  background: #bd1933;
  color: ${({ theme }) => theme.color.white};
  svg {
    width: ${rem(22)};
    height: ${rem(22)};
  }
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const Footer = styled.div`
  flex: 0 0 auto;
  padding: ${rem(12)};
  border-top: ${rem(1)} solid #e6e6e6;
  background: ${({ theme }) => theme.color.white};
  box-shadow: 0 -${rem(8)} ${rem(24)} rgba(0, 0, 0, 0.1);
`;

const ApplyButton = styled.button`
  appearance: none;
  border: none;
  cursor: pointer;
  width: 100%;
  font-family: inherit;
  background: ${({ theme }) => theme.color.secondary};
  color: #171717;
  /* Figma "Použiť filter" uses the same 14px/600 text as the filter buttons
     (style_M337SI) — not 16px/700. */
  font-size: ${rem(14)};
  font-weight: 600;
  border-radius: ${rem(8)};
  padding: ${rem(12)} ${rem(24)};
  box-shadow: 0 ${rem(2)} ${rem(8)} rgba(0, 0, 0, 0.05);
`;

export default MobileFilterDrawer;
